import { z } from 'zod';
import { bandForSsidPort, ssidPortsForBand } from './wifi-band.js';

/**
 * Device config presets — the device-SIDE counterpart to the authorise presets.
 *
 * An authorise preset is what the OLT is told about a subscriber: line and
 * service profile, speed package, VLANs, zone. A device preset is what the ONU
 * itself is told: its SSID and key, its radios, how its WAN gets an address,
 * what its LAN hands out. The two never overlap and are deliberately separate
 * objects, because the failure they prevent is different — one puts a
 * subscriber on the wrong package, the other puts the wrong WiFi key on their
 * router.
 *
 * The critical field is `target`. There are two device-side write paths in
 * this platform and they write COMPLETELY different things:
 *
 *   tr069 — parameter writes through the ACS, against the paths the CPE
 *           itself reports (`InternetGatewayDevice.LANDevice.1.
 *           WLANConfiguration.1.SSID`, …). Needs the ONU to have informed.
 *   omci  — CLI through the chassis, in `pon-onu-mng`. Needs no ACS at all,
 *           and is ZTE-only because a Huawei OLT has no ONT wifi command.
 *
 * A preset that is silently wrong for the target is the failure this design is
 * against, so a preset carries which path it speaks and the resolver refuses
 * rather than half-applies. `vendor` narrows it further where the CLI grammar
 * is vendor-specific.
 *
 * Every field below maps to a write this platform can actually perform today.
 * Nothing is modelled that the platform cannot apply — a preset field with no
 * write behind it is a promise to an operator that quietly does nothing.
 */

export const devicePresetTargetSchema = z.enum(['tr069', 'omci']);
export type DevicePresetTarget = z.infer<typeof devicePresetTargetSchema>;

export const devicePresetVendorSchema = z.enum(['zte', 'huawei']);
export type DevicePresetVendor = z.infer<typeof devicePresetVendorSchema>;

/** The WPA modes the ZTE `ssid auth wpa` line accepts. OMCI only. */
export const devicePresetWpaModeSchema = z.enum(['wpa-psk', 'wpa2-psk', 'wpa-wpa2-psk']);
export type DevicePresetWpaMode = z.infer<typeof devicePresetWpaModeSchema>;

/**
 * WiFi.
 *
 * The passphrase is NOT here: it lives in its own encrypted column so that
 * "the settings blob is safe to return" is structural rather than something a
 * mapper has to remember. Same rule the WiFi credential store already follows.
 *
 * Both targets address a BAND, and each resolves it from the side that knows.
 * TR-069 reads the band off the WLAN instance the CPE reports
 * (`extractWlans`). OMCI has no such reading — the chassis names SSID
 * interfaces `wifi_0/1` … `wifi_0/8` and never says which radio one is on — so
 * it resolves through `SSID_BAND_PORTS`, the operator's own layout (1-4 =
 * 2.4 GHz, 5-8 = 5 GHz). A preset therefore carries `ssid24` and `ssid5g` on
 * both paths, and `ssidPort24` / `ssidPort5g` exist only to move an OMCI
 * write off the primary interface of its band — never across bands, which is
 * refused rather than accepted and rendered onto the wrong radio.
 */
export const devicePresetWifiSchema = z.object({
  /** The 2.4 GHz radio. Null leaves it alone. */
  radio24: z.boolean().nullable().default(null),
  /** The 5 GHz radio. Not every model has one. */
  radio5g: z.boolean().nullable().default(null),
  /** 1–13. OMCI only — no writable channel path is resolved over TR-069. */
  channel24: z.number().int().min(1).max(13).nullable().default(null),
  /** The 2.4 GHz network's name. TR-069: the first 2.4 GHz WLAN. OMCI: `ssidPort24`. */
  ssid24: z.string().trim().min(1).max(32).nullable().default(null),
  /** The 5 GHz network's name. TR-069: the first 5 GHz WLAN. OMCI: `ssidPort5g`. */
  ssid5g: z.string().trim().min(1).max(32).nullable().default(null),
  /**
   * Which SSID interface each band's name is written to. OMCI only, and only
   * to move off the band's primary (`wifi_0/1` / `wifi_0/5`) — a port from the
   * other band is refused, never rendered. TR-069 addresses WLAN instances the
   * CPE reports and has no use for either.
   */
  ssidPort24: z
    .string()
    .regex(/^wifi_\d{1,2}\/\d{1,2}$/, 'An SSID port looks like wifi_0/1')
    .nullable()
    .default(null),
  ssidPort5g: z
    .string()
    .regex(/^wifi_\d{1,2}\/\d{1,2}$/, 'An SSID port looks like wifi_0/5')
    .nullable()
    .default(null),
  /** Broadcast the name or not. OMCI only. */
  hidden: z.boolean().nullable().default(null),
  /** Which WPA flavour the key is set with. OMCI only; the chassis defaults to mixed. */
  wpaMode: devicePresetWpaModeSchema.nullable().default(null),
});
export type DevicePresetWifi = z.infer<typeof devicePresetWifiSchema>;

/**
 * The subscriber WAN.
 *
 * DHCP and PPPoE only. `static` and the three fields that go with it stay in
 * the schema so a row written before the rule still parses and can be read
 * back — but `devicePresetRefusals` refuses them on save and blocks them at
 * apply, so they can never be chosen and can never be written. A static
 * address belongs to one subscriber; a preset is applied to many.
 */
export const devicePresetWanSchema = z.object({
  /** `static` is representable for legacy rows only — never saveable, never applied. */
  mode: z.enum(['dhcp', 'pppoe', 'static']).nullable().default(null),
  vlanId: z.number().int().min(1).max(4094).nullable().default(null),
  /** The PPPoE account name. The password is stored encrypted, separately. */
  pppoeUsername: z.string().trim().min(1).max(120).nullable().default(null),
  /** Legacy only — refused by `devicePresetRefusals`. */
  staticIp: z.string().trim().max(45).nullable().default(null),
  subnetMask: z.string().trim().max(45).nullable().default(null),
  gateway: z.string().trim().max(45).nullable().default(null),
  /**
   * WAN DNS servers, comma-separated as the CPE takes them.
   *
   * TR-069 only, and written as a plain parameter write against the path the
   * CPE reports — not as part of an addressing change. It used to ride along
   * with a static WAN, which would have made it unreachable the moment static
   * was refused; a subscriber on DHCP can be given resolvers just the same.
   */
  dns: z.string().trim().max(120).nullable().default(null),
});
export type DevicePresetWan = z.infer<typeof devicePresetWanSchema>;

/**
 * The ONU's own LAN.
 *
 * TR-069 only, and again because of what exists rather than what was skipped:
 * the chassis can READ an OMCI ONU's DHCP pool (`show gpon remote-onu
 * dhcp-server`) and has no verified command to write one.
 */
export const devicePresetLanSchema = z.object({
  ip: z.string().trim().max(45).nullable().default(null),
  subnetMask: z.string().trim().max(45).nullable().default(null),
  dhcpEnabled: z.boolean().nullable().default(null),
  dhcpStart: z.string().trim().max(45).nullable().default(null),
  dhcpEnd: z.string().trim().max(45).nullable().default(null),
  dhcpLeaseTime: z.number().int().min(60).max(2_592_000).nullable().default(null),
  /** What the LAN pool hands out as DNS. */
  dns: z.string().trim().max(120).nullable().default(null),
});
export type DevicePresetLan = z.infer<typeof devicePresetLanSchema>;

export const devicePresetSettingsSchema = z.object({
  wifi: devicePresetWifiSchema.default({}),
  wan: devicePresetWanSchema.default({}),
  lan: devicePresetLanSchema.default({}),
});
export type DevicePresetSettings = z.infer<typeof devicePresetSettingsSchema>;

/** Nothing set at all — a preset that would write nothing is refused. */
export function devicePresetIsEmpty(
  settings: DevicePresetSettings,
  hasWifiPassphrase = false,
): boolean {
  if (hasWifiPassphrase) return false;
  for (const group of [settings.wifi, settings.wan, settings.lan]) {
    for (const value of Object.values(group)) {
      if (value !== null && value !== undefined) return false;
    }
  }
  return true;
}

/**
 * Things a preset must never carry, whichever path it speaks.
 *
 * Separate from `devicePresetFieldIssues` because the two mean different
 * things at apply time. A field the target cannot write is skipped and the
 * rest of the preset is still correct, so it warns. Something in here makes
 * the preset itself wrong, so it blocks — nothing is written at all.
 *
 * A static WAN address is the case that defines the distinction, and the
 * operator's ruling (2026-08-25). A preset is by definition applied more than
 * once; a static address is by definition applied once. Carrying one means the
 * second subscriber it is applied to gets the first subscriber's address, and
 * two ONUs on one IP is an outage for both that presents as neither. This used
 * to be a loud warning, which depends on somebody reading it; a refusal does
 * not.
 *
 * Deliberately scoped to PRESETS. Configuring one named ONU with a static WAN
 * is untouched and stays exactly where it was — `POST /devices/:id/wan` and
 * the ONU page's WAN dialog. The restriction is about reuse, not about static
 * addressing.
 */
export function devicePresetRefusals(settings: DevicePresetSettings): string[] {
  const issues: string[] = [];
  const { wan } = settings;

  if (wan.mode === 'static') {
    issues.push('A preset cannot set a static WAN. A preset is applied more than once and a static address belongs to one subscriber — set it on that ONU’s own WAN instead.');
  }
  for (const [field, label] of [
    ['staticIp', 'a static WAN address'],
    ['subnetMask', 'a WAN subnet mask'],
    ['gateway', 'a WAN gateway'],
  ] as const) {
    if (wan[field] !== null) {
      issues.push(`A preset cannot carry ${label} — it belongs to one subscriber. Set it on that ONU’s own WAN instead.`);
    }
  }
  return issues;
}

/**
 * Fields this preset carries that its own target cannot write.
 *
 * One rule read in two places: the API refuses a preset carrying them at
 * create/update time, and the resolver warns about them when an older row
 * still holds one. Splitting that judgement in two is how the two come to
 * disagree, and a preset the page accepted but the apply silently dropped is
 * exactly the "silently wrong for the target" failure this feature exists to
 * design against.
 */
export function devicePresetFieldIssues(
  target: DevicePresetTarget,
  settings: DevicePresetSettings,
): string[] {
  const issues: string[] = [];
  const { wifi, wan, lan } = settings;
  // A chassis-written SSID is ONE word (ZTE `ssid ctrl <port> name WORD`;
  // a space answered `^`, quotes `%Error 20201` — gachie1, 2026-09-09). The
  // CPE's own WLAN parameter over TR-069 takes spaces, so only OMCI is held to it.
  if (target === 'omci') {
    for (const key of ['ssid24', 'ssid5g'] as const) {
      const name = wifi[key];
      if (typeof name === 'string' && /\s/.test(name)) {
        issues.push(
          `The ${key === 'ssid24' ? '2.4 GHz' : '5 GHz'} SSID "${name}" has a space. An OMCI-written SSID is one word of 1-32 characters — the ZTE chassis answers '^' to a space and '%Error 20201' to quotes. Use a dash or drop the space.`,
        );
      }
    }
  }

  if (target === 'tr069') {
    if (wifi.channel24 !== null) {
      issues.push('A WiFi channel is written over OMCI only — no CPE in this fleet exposes a writable channel path over TR-069.');
    }
    if (wifi.hidden !== null) {
      issues.push('Hiding an SSID is written over OMCI only — the ACS write path resolves SSID, passphrase and enable, and nothing else.');
    }
    if (wifi.wpaMode !== null) {
      issues.push('A WPA mode is written over OMCI only — over TR-069 the CPE keeps whatever it already runs.');
    }
    if (wifi.ssidPort24 !== null || wifi.ssidPort5g !== null) {
      issues.push('An SSID port names a chassis interface (wifi_0/1); over TR-069 a band resolves to the WLAN instance the CPE itself reports, so there is nothing to point at.');
    }
  } else {
    // The chassis takes a channel only on the `wifi enable` line, so a preset
    // that sets one without turning the radio on would have it silently
    // dropped by the renderer — which reads as "the channel did not change".
    if (wifi.channel24 !== null && wifi.radio24 !== true) {
      issues.push('A WiFi channel reaches the chassis on the `wifi enable` line, so a preset setting a channel must also turn the 2.4 GHz radio on.');
    }
    /**
     * A port from the other band is refused rather than rendered.
     *
     * This is the whole reason the two port fields are separate. `ssidPort5g:
     * "wifi_0/2"` reads like a small mistake and would rename the subscriber's
     * 2.4 GHz network to the 5 GHz name — the one failure a band mapping
     * exists to prevent, arriving through the field that was meant to prevent
     * it. An interface outside the layout altogether is refused for the same
     * reason: `bandForSsidPort` answering null means this platform does not
     * know which radio it is, and it will not guess.
     */
    for (const [field, band] of [
      ['ssidPort24', '2.4G'],
      ['ssidPort5g', '5G'],
    ] as const) {
      const port = wifi[field];
      if (port === null) continue;
      const actual = bandForSsidPort(port);
      if (actual === null) {
        issues.push(`${port} is not an SSID interface in this fleet's layout — ${ssidPortsForBand('2.4G').join(', ')} are 2.4 GHz and ${ssidPortsForBand('5G').join(', ')} are 5 GHz.`);
      } else if (actual !== band) {
        issues.push(`${port} is a ${actual} interface, so it cannot carry the ${band} name. ${band} runs on ${ssidPortsForBand(band).join(', ')}.`);
      }
    }
    // Static addressing is refused for every preset by `devicePresetRefusals`,
    // so it is not repeated here. What remains is genuinely a path question:
    // the OMCI WAN line takes a mode and a VLAN and nothing else.
    if (wan.dns !== null) {
      issues.push('WAN DNS servers are written over TR-069 — the OMCI `wan-ip` line takes a mode and a VLAN and nothing else.');
    }
    if (Object.values(lan).some((v) => v !== null)) {
      issues.push('LAN settings are written over TR-069 only — the chassis can read an ONU’s DHCP pool but has no verified command to write one.');
    }
  }
  return issues;
}

export const devicePresetSchema = z.object({
  id: z.string(),
  orgId: z.string().nullable(),
  name: z.string(),
  description: z.string().nullable(),
  target: devicePresetTargetSchema,
  /** Null means any vendor; only meaningful on an OMCI preset. */
  vendor: devicePresetVendorSchema.nullable(),
  settings: devicePresetSettingsSchema,
  /** Whether a key is held — never the key. */
  hasWifiPassphrase: z.boolean(),
  hasPppoePassword: z.boolean(),
  createdAt: z.number().int(),
  createdBy: z.string().nullable(),
  updatedAt: z.number().int(),
});
export type DevicePreset = z.infer<typeof devicePresetSchema>;

const createDevicePresetObject = z.object({
  name: z.string().trim().min(1).max(60),
  description: z.string().trim().max(200).nullable().optional(),
  target: devicePresetTargetSchema,
  vendor: devicePresetVendorSchema.nullable().optional(),
  settings: devicePresetSettingsSchema.default({}),
  /**
   * Plaintext on the wire and nowhere else: stored AES-GCM encrypted, masked
   * in every preview, never returned by any read.
   */
  wifiPassphrase: z.string().min(8).max(63).nullable().optional(),
  pppoePassword: z.string().min(1).max(128).nullable().optional(),
});

export const createDevicePresetSchema = createDevicePresetObject.superRefine((preset, ctx) => {
  /**
   * A chassis-bound SSID is ONE word. `ssid ctrl <port> name` on a ZTE takes
   * `WORD  SSID name (1-32 character(s))`: `name Mymanga Public WiFi` answered
   * `^` and the quoted form `%Error 20201` (gachie1, 2026-09-09). A TR-069
   * preset writes the CPE's own WLAN parameter, where spaces are fine, so only
   * the OMCI target is held to the chassis's rule — and at the boundary, so a
   * preset that can never land is refused when it is saved, not on every ONU.
   */
  if (preset.target !== 'omci') return;
  for (const key of ['ssid24', 'ssid5g'] as const) {
    const name = preset.settings?.wifi?.[key];
    if (typeof name === 'string' && /\s/.test(name)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['settings', 'wifi', key],
        message:
          'An OMCI-written SSID name is one word: the ZTE chassis takes `ssid ctrl <port> name WORD` (1-32 characters) and answers `^` to a space and `%Error 20201` to quotes. Use a dash or drop the space.',
      });
    }
  }
});
export type CreateDevicePresetInput = z.infer<typeof createDevicePresetSchema>;

/** Every field optional; an absent one is left as it is, `null` clears it. */
export const updateDevicePresetSchema = createDevicePresetObject.partial().omit({ target: true });
export type UpdateDevicePresetInput = z.infer<typeof updateDevicePresetSchema>;

/**
 * Applying is explicit and per-target: ONE ONU, named by the caller, dry-run
 * by default. There is deliberately no list here — bulk application is a
 * separately guarded feature, and an endpoint that takes an array of ONUs is
 * exactly how it stops being one.
 */
export const applyDevicePresetSchema = z.object({
  onuId: z.string().min(1),
  dryRun: z.boolean().default(true),
});
export type ApplyDevicePresetInput = z.infer<typeof applyDevicePresetSchema>;

/** One TR-069 write, with its value already masked when it is a secret. */
export const devicePresetParameterSchema = z.object({
  path: z.string(),
  value: z.string(),
  secret: z.boolean(),
});

/**
 * What applying this preset to this ONU would do — the receipt an operator
 * reads before anything is written, and the same shape the apply returns.
 */
export const devicePresetPlanSchema = z.object({
  presetId: z.string(),
  presetName: z.string(),
  target: devicePresetTargetSchema,
  onuId: z.string(),
  onuSerial: z.string(),
  /** False when `blockers` is non-empty; nothing is written either way. */
  applicable: z.boolean(),
  /** Why this preset cannot be applied to this ONU. */
  blockers: z.array(z.string()),
  /** Applied anyway, but worth reading first. */
  warnings: z.array(z.string()),
  /** One line per thing this changes, in an operator's words. */
  summary: z.array(z.string()),
  /** OMCI: the exact chassis commands, passphrases already masked. */
  commands: z.array(z.string()),
  /** TR-069: the exact parameter writes, secret values already masked. */
  parameterWrites: z.array(devicePresetParameterSchema),
  /**
   * TR-069: the WAN switch, which is an object rebuild rather than a
   * parameter write, so it is reported on its own. Never carries a password.
   */
  wan: z
    .object({
      mode: z.enum(['dhcp', 'pppoe', 'static']),
      vlanId: z.number().int().nullable(),
      pppoeUsername: z.string().nullable(),
      hasPassword: z.boolean(),
    })
    .nullable(),
  /** Set only on a real apply: what the chassis or the ACS answered. */
  transcript: z.string().nullable(),
  applied: z.boolean(),
});
export type DevicePresetPlan = z.infer<typeof devicePresetPlanSchema>;
